import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPHP } from "@/lib/format";
import { resolveImageUrl } from "@/lib/api";

export default function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-12">
          <div className="overline mb-3">Your Cart</div>
          <h1 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight" data-testid="cart-title">
            Review your items
          </h1>
        </div>
      </section>

      <section className="bg-msr-surface">
        <div className="msr-container py-12">
          {items.length === 0 ? (
            <div className="msr-card text-center py-16" data-testid="cart-empty">
              <ShoppingBag size={36} className="text-msr-slate mx-auto mb-5" />
              <h2 className="font-heading font-bold text-2xl mb-2">Your cart is empty</h2>
              <p className="text-msr-slate mb-6">Discover MSR hardware, software, and ready-to-deploy bundles.</p>
              <Link to="/shop" className="btn-msr-primary">Browse Shop</Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 space-y-3">
                {items.map((i) => (
                  <div key={i.id} className="msr-card flex flex-col sm:flex-row gap-5 items-start" data-testid={`cart-item-${i.id}`}>
                    <div className="h-24 w-24 bg-msr-mist border border-msr-line overflow-hidden flex-shrink-0">
                      {i.image && <img src={resolveImageUrl(i.image)} alt={i.name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/shop/${i.slug}`} className="font-heading font-bold text-lg hover:text-msr-blue">{i.name}</Link>
                      <div className="text-xs uppercase tracking-wider text-msr-slate mt-0.5">SKU: {i.sku}</div>
                      <div className="text-msr-blue font-heading font-extrabold mt-2">{formatPHP(i.price)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-msr-line">
                        <button onClick={() => update(i.id, i.quantity - 1)} className="p-2 hover:bg-msr-mist" data-testid={`cart-decrease-${i.id}`}><Minus size={12} /></button>
                        <span className="px-3 text-sm font-semibold" data-testid={`cart-qty-${i.id}`}>{i.quantity}</span>
                        <button onClick={() => update(i.id, i.quantity + 1)} className="p-2 hover:bg-msr-mist" data-testid={`cart-increase-${i.id}`}><Plus size={12} /></button>
                      </div>
                      <button onClick={() => remove(i.id)} className="text-msr-slate hover:text-red-500" data-testid={`cart-remove-${i.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="font-heading font-extrabold text-msr-ink sm:ml-auto" data-testid={`cart-line-total-${i.id}`}>
                      {formatPHP(i.price * i.quantity)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:col-span-4">
                <div className="msr-card border-t-4 border-t-msr-blue" data-testid="cart-summary">
                  <div className="overline mb-4">Order summary</div>
                  <div className="flex justify-between text-sm py-2 border-b border-msr-line">
                    <span className="text-msr-slate">Subtotal</span>
                    <span className="font-semibold" data-testid="cart-subtotal">{formatPHP(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-2 border-b border-msr-line">
                    <span className="text-msr-slate">Shipping</span>
                    <span className="font-semibold">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between py-4 mt-1">
                    <span className="font-heading font-extrabold text-lg">Estimated total</span>
                    <span className="font-heading font-extrabold text-2xl text-msr-blue">{formatPHP(subtotal)}</span>
                  </div>
                  <button onClick={() => navigate("/checkout")} className="w-full btn-msr-primary mt-2" data-testid="cart-checkout-btn">
                    Proceed to Checkout
                  </button>
                  <Link to="/shop" className="block text-center mt-3 text-xs uppercase tracking-wider text-msr-slate hover:text-msr-blue">
                    or continue shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
